import { useEffect } from "react";
import { useFormikContext } from "formik";
import { Radio, Tag } from "antd";
import { InfoCircleOutlined, UserOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import trans from "../../../utils";
import { Input, InputDate, TextAreaInput } from "../../Helpers/Input/Input";
import {
	InputSelect,
	TimePickerInput,
} from "../../Helpers/Input/RefactoredInput";
import { useLocalForms } from "../../../hooks/useForms";
import { usePois } from "../../../hooks/usePOIs";
import { useTechnicians } from "../../../hooks/useTechnicians";
import { WEEK_DAYS } from "../WeekDayChips";

const taskTypeOptions = [
	{ label: trans("inspection"), value: "inspection" },
	{ label: trans("delivery"), value: "delivery" },
	{ label: trans("survey"), value: "survey" },
	{ label: trans("maintenance"), value: "maintenance" },
	{ label: trans("other"), value: "other" },
];

const priorityOptions = [
	{ label: trans("low"), value: "low" },
	{ label: trans("medium"), value: "medium" },
	{ label: trans("high"), value: "high" },
];

const flowOptions = [
	{
		label: trans("Sequential Task"),
		value: "sequential",
		description: trans(
			"Must finish all forms step by step to complete the task, end only when all are done"
		),
	},
	{
		label: trans("Flexible Task"),
		value: "flexible",
		description: trans(
			"Complete forms freely and end anytime, task can end with pending forms"
		),
	},
];

const ALL_DAYS = WEEK_DAYS.map((d) => d.value);

const RecurringPlannerFormBody = ({ isEditing }) => {
	const {
		options: usersOptions,
		isFetching: techLoading,
		refetch: refetchTech,
	} = useTechnicians();
	const {
		options: formOptions,
		isFetching: formLoading,
		refetch: refetchForms,
	} = useLocalForms();
	const {
		options: poisOptions,
		isFetching: poiLoading,
		refetch: refetchPois,
	} = usePois();

	useEffect(() => {
		if (isEditing) {
			refetchPois();
			refetchForms();
			refetchTech();
		}
	}, [isEditing]);

	const formikContext = useFormikContext();

	if (!formikContext) {
		return (
			<div className="alert alert-danger">
				{trans("Form context error. Please refresh the page.")}
			</div>
		);
	}

	const {
		values,
		handleChange,
		handleBlur,
		errors,
		touched,
		setFieldValue,
		setFieldTouched,
	} = formikContext;

	const toggleDay = (day) => {
		const current = values.daysOfWeek || [];
		const next = current.includes(day)
			? current.filter((d) => d !== day)
			: [...current, day];
		setFieldValue("daysOfWeek", next);
		setFieldTouched("daysOfWeek", true, false);
	};

	const daysError = touched.daysOfWeek && errors.daysOfWeek;

	return (
		<div className="form-section">
			<div className="row">
				<div className="col-lg-6">
					<Input
						title={trans("Planner Name")}
						type="text"
						placeholder={trans("Ex. Daily Site Inspection")}
						formstyle="form-input"
						id="name"
						name="name"
						value={values.name}
						onChange={handleChange}
						onBlur={handleBlur}
						errors={errors.name}
						touched={touched.name}
						max={128}
					/>
				</div>
				<div className="col-lg-6">
					<InputSelect
						id="type"
						name="type"
						label={trans("Type")}
						placeholder={trans("Select Task Type")}
						options={taskTypeOptions}
						value={values.type || undefined}
						onChange={(val) => setFieldValue("type", val)}
						onBlur={() => setFieldTouched("type", true)}
						errors={errors.type}
						touched={touched.type}
						formstyle="form-input select-z-index"
					/>
				</div>
				<div className="col-lg-6">
					<InputSelect
						id="priority"
						name="priority"
						label={trans("Priority")}
						placeholder={trans("Select Priority")}
						options={priorityOptions}
						value={values.priority}
						onChange={(val) => setFieldValue("priority", val)}
						onBlur={() => setFieldTouched("priority", true)}
						errors={errors.priority}
						touched={touched.priority}
						formstyle="form-input select-z-index"
					/>
				</div>
				<div className="col-lg-6">
					<InputSelect
						id="assignedToId"
						name="assignedToId"
						label={trans("Assign to")}
						placeholder={trans("Select field staff")}
						options={usersOptions}
						loading={techLoading}
						value={values?.assignedToId || undefined}
						onChange={(val) => setFieldValue("assignedToId", val || "")}
						onBlur={() => setFieldTouched("assignedToId", true)}
						errors={errors.assignedToId}
						touched={touched.assignedToId}
						formstyle="form-input select-z-index"
						optionRender={(option) => (
							<div
								style={{ display: "flex", alignItems: "center", gap: "8px" }}
							>
								{option.data.avatar ? (
									<img
										src={option.data.avatar}
										alt={option.label}
										style={{ width: 24, height: 24, borderRadius: "50%" }}
									/>
								) : (
									<UserOutlined
										style={{ width: 24, height: 24, borderRadius: "50%" }}
									/>
								)}
								<span>{option.label}</span>
							</div>
						)}
					/>
				</div>
				<div className="col-lg-6">
					<InputSelect
						id="poiId"
						name="poiId"
						label={trans("POI")}
						placeholder={trans("Select POI")}
						options={poisOptions}
						loading={poiLoading}
						value={values.poiId || undefined}
						onChange={(val) => setFieldValue("poiId", val ? Number(val) : "")}
						onBlur={() => setFieldTouched("poiId", true)}
						errors={errors.poiId}
						touched={touched.poiId}
						formstyle="form-input select-z-index"
					/>
				</div>
				<div className="col-lg-6">
					<InputSelect
						id="forms"
						name="forms"
						label={trans("Assign Form")}
						placeholder={trans("Select Forms")}
						options={formOptions.map((opt) => {
							const index = values.forms.indexOf(opt.value);
							return {
								...opt,
								label: (
									<span
										style={{
											display: "flex",
											justifyContent: "space-between",
											alignItems: "center",
										}}
									>
										{opt.label}
										{index !== -1 && (
											<Tag
												color="green"
												style={{ marginLeft: "8px", fontWeight: "bold" }}
											>
												{index + 1}
											</Tag>
										)}
									</span>
								),
							};
						})}
						loading={formLoading}
						mode="multiple"
						value={values.forms}
						onChange={(val) => setFieldValue("forms", val)}
						onBlur={() => setFieldTouched("forms", true)}
						errors={errors.forms}
						touched={touched.forms}
						formstyle="form-input select-z-index"
						infoTooltip={trans(
							"The forms flow follows the same arrangement of choosing forms. Make sure you are entering them in the correct order."
						)}
					/>
				</div>

				{/* Recurrence Schedule */}
				<div className="col-lg-12 mt-3">
					<p className="fs-16 fw-500 brand-700 text-capitalize me-2">
						{trans("Recurrence Schedule")}
					</p>
					<div
						className="p-3 rounded-3"
						style={{ backgroundColor: "#f8faf9", border: "1px solid #e6ece8" }}
					>
						<div className="form-group mb-2">
							<label className="form-label d-flex align-items-center justify-content-between">
								<span>{trans("Repeat on")}</span>
								<span className="d-flex gap-3">
									<small
										style={{ color: "#0f6936", cursor: "pointer" }}
										onClick={() => {
											setFieldValue("daysOfWeek", [...ALL_DAYS]);
											setFieldTouched("daysOfWeek", true, false);
										}}
									>
										{trans("Every day")}
									</small>
									<small
										style={{ color: "#8c8c8c", cursor: "pointer" }}
										onClick={() => {
											setFieldValue("daysOfWeek", []);
											setFieldTouched("daysOfWeek", true, false);
										}}
									>
										{trans("Clear")}
									</small>
								</span>
							</label>
							<div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
								{WEEK_DAYS.map((day) => {
									const active = values.daysOfWeek?.includes(day.value);
									return (
										<button
											key={day.value}
											type="button"
											title={trans(day.full)}
											onClick={() => toggleDay(day.value)}
											style={{
												width: 42,
												height: 42,
												borderRadius: "50%",
												fontSize: 12,
												fontWeight: 600,
												cursor: "pointer",
												transition: "all 0.15s ease",
												color: active ? "#fff" : "#5c6670",
												backgroundColor: active ? "#0f6936" : "#fff",
												border: active
													? "1px solid #0f6936"
													: "1px solid #d4dad6",
											}}
										>
											{trans(day.short)}
										</button>
									);
								})}
							</div>
							{daysError && <div className="validation">{daysError}</div>}
						</div>

						<div className="row">
							<div className="col-lg-6">
								<InputDate
									title={trans("Start Date")}
									id="startDate"
									name="startDate"
									onBlur={() => setFieldTouched("startDate", true)}
									onChange={(value) =>
										setFieldValue(
											"startDate",
											value ? dayjs(value).format("YYYY-MM-DD") : ""
										)
									}
									errors={errors.startDate}
									touched={touched.startDate}
									value={values.startDate || ""}
									min={isEditing ? undefined : dayjs()}
								/>
							</div>
							<div className="col-lg-6">
								<InputDate
									title={`${trans("End Date")} (${trans("optional")})`}
									id="endDate"
									name="endDate"
									onBlur={() => setFieldTouched("endDate", true)}
									onChange={(value) =>
										setFieldValue(
											"endDate",
											value ? dayjs(value).format("YYYY-MM-DD") : ""
										)
									}
									errors={errors.endDate}
									touched={touched.endDate}
									value={values.endDate || ""}
									min={values.startDate ? dayjs(values.startDate) : dayjs()}
								/>
							</div>
							<div className="col-lg-6">
								<TimePickerInput
									title={trans("Start Time")}
									id="startTime"
									name="startTime"
									formstyle="form-input"
									value={values.startTime}
									onChange={(time) => setFieldValue("startTime", time)}
									onBlur={() => setFieldTouched("startTime", true)}
									errors={errors.startTime}
									touched={touched.startTime}
								/>
							</div>
							<div className="col-lg-6">
								<Input
									title={trans("Duration (minutes)")}
									type="number"
									placeholder={trans("e.g. 90")}
									formstyle="form-input"
									id="expectedDuration"
									name="expectedDuration"
									value={values.expectedDuration}
									onChange={(e) => {
										const value = parseInt(e.target.value, 10);
										setFieldValue(
											"expectedDuration",
											isNaN(value) ? "" : value
										);
									}}
									onBlur={handleBlur}
									errors={errors.expectedDuration}
									touched={touched.expectedDuration}
									min={1}
									max={1440}
								/>
							</div>
						</div>
						<small className="text-muted d-flex align-items-center">
							<InfoCircleOutlined
								style={{ color: "#8c8c8c", marginRight: "6px" }}
							/>
							{trans(
								"A task will be generated automatically on each selected day at the chosen time, within the date range."
							)}
						</small>
					</div>
				</div>

				<div className="col-lg-12 mt-3">
					<TextAreaInput
						title={trans("Notes")}
						type="textarea"
						placeholder={trans("Add additional task details...")}
						formstyle="form-input"
						id="notes"
						name="notes"
						value={values.notes}
						onChange={handleChange}
						onBlur={handleBlur}
						errors={errors.notes}
						touched={touched.notes}
						rows={3}
					/>
				</div>

				{/* Select Flow */}
				<div className="col-lg-12 mt-3">
					<p className="fs-16 fw-500 brand-700 text-capitalize me-2">
						{trans("Select Flow")}
					</p>
					<Radio.Group
						onChange={(e) => setFieldValue("flowType", e.target.value)}
						value={values.flowType}
						className="d-flex flex-column gap-2"
					>
						{flowOptions.map((option) => (
							<div
								key={option.value}
								className="d-flex align-items-start p-3 rounded-3"
								style={{ cursor: "pointer" }}
								onClick={() => setFieldValue("flowType", option.value)}
							>
								<Radio
									value={option.value}
									style={{
										transform: "scale(1.1)",
										accentColor: "#52c41a",
										marginRight: "12px",
									}}
								/>
								<div>
									<div
										className="fw-semibold"
										style={{ color: "#000", fontSize: "15px" }}
									>
										{trans(option.label)}
									</div>
									<div
										className="text-muted d-flex align-items-center mt-1"
										style={{ fontSize: "13px" }}
									>
										<InfoCircleOutlined
											style={{ color: "#8c8c8c", marginRight: "6px" }}
										/>
										{trans(option.description)}
									</div>
								</div>
							</div>
						))}
					</Radio.Group>
				</div>
			</div>
		</div>
	);
};

export default RecurringPlannerFormBody;
